import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Palmtree } from 'lucide-react';
import Button from '../components/Button';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import './Login.css';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const toast = useToast();
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!token?.trim()) {
            setError('Link inválido ou expirado.');
            return;
        }

        setLoading(true);
        try {
            const { data: ok, error: rpcError } = await supabase.rpc('reset_password', {
                p_token: token,
                p_new_password: password,
            });

            if (rpcError) {
                toast.error(rpcError.message || 'Não foi possível redefinir a senha.');
                return;
            }

            if (ok) {
                toast.success('Senha alterada. Faça login com a nova senha.');
                navigate('/login');
            } else {
                setError('Link inválido ou expirado.');
            }
        } finally {
            setLoading(false);
        }
    };

    const invalidLink = !token?.trim();

    return (
        <div className="login-page">
            <div className="login-background">
                <div className="bg-shape shape-1"></div>
                <div className="bg-shape shape-2"></div>
                <div className="bg-shape shape-3"></div>
            </div>

            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-icon">
                            <Palmtree size={32} />
                        </div>
                        <h1>Nova senha</h1>
                        <p>Defina uma nova senha para sua conta.</p>
                    </div>

                    {invalidLink ? (
                        <>
                            <div className="error-message">
                                Este link é inválido ou expirou. Solicite um novo email em &quot;Esqueceu a senha?&quot;.
                            </div>
                            <div className="login-footer login-footer-links-stacked">
                                <p><Link to="/forgot-password">Solicitar novo link</Link></p>
                                <p><Link to="/login">Voltar ao login</Link></p>
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleSubmit} className="login-form">
                            {error && <div className="error-message">{error}</div>}

                            <div className="input-group">
                                <div className="input-icon">
                                    <Lock size={20} />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Nova senha"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>

                            <Button
                                type="submit"
                                variant="primary"
                                fullWidth
                                disabled={loading}
                                isLoading={loading}
                                className="login-btn"
                            >
                                {loading ? 'Salvando...' : 'Redefinir senha'}
                            </Button>
                        </form>
                    )}

                    {!invalidLink && (
                        <div className="login-footer">
                            <p><Link to="/login">Voltar ao login</Link></p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
